import { assign, setup } from 'xstate';
import {
  dismissAlertAction,
  hideAlertAction,
  nextAlertAction,
  setActiveAlertAction,
} from './actions';
import type { AlertViewEvents } from './events';
import type { AlertViewContext, AlertViewInput } from './types';

export const alertView = setup({
  types: {
    context: {} as AlertViewContext,
    events: {} as AlertViewEvents,
    input: {} as AlertViewInput,
  },
  actions: {
    showAlert: assign(setActiveAlertAction),
    hideAlert: assign(hideAlertAction),
    dismissAlert: assign(dismissAlertAction),
    nextAlert: assign(nextAlertAction),
  },
  delays: {
    // Default to 5000ms when a caller dispatches an alert without a timeout —
    // ensures auto-dismiss is the floor behavior even if a future caller bypasses
    // sendSuccessAlert/sendErrorAlert.
    ALERT_TIMEOUT: ({ context }) => context.activeAlert?.timeout ?? 5000,
  },
}).createMachine({
  id: 'alertView',
  context: ({ input }): AlertViewContext => ({
    alerts: [],
    activeAlert: null,
    ...input,
  }),
  initial: '$_IDLE',
  on: {
    // Session boundary: forget every alert, queued or showing. Leaving the current
    // state also cancels its auto-dismiss timer.
    ON_RESET_ALERTS: {
      target: '.$_IDLE',
      reenter: true,
      actions: assign({ alerts: [], activeAlert: null }),
    },
    ON_SHOW_ALERT: [
      {
        // Already showing — just queue, don't re-enter $_SHOWING (preserves timeout)
        guard: ({ context }) => !!context.activeAlert,
        actions: assign(({ context, event }) => {
          const showEvent = event as Extract<AlertViewEvents, { type: 'ON_SHOW_ALERT' }>;
          const newAlert = { id: crypto.randomUUID(), createdAt: new Date(), ...showEvent.payload };
          return { alerts: [...context.alerts, newAlert] };
        }),
      },
      {
        // No active alert — show immediately and enter $_SHOWING
        actions: assign(({ event }) => {
          const showEvent = event as Extract<AlertViewEvents, { type: 'ON_SHOW_ALERT' }>;
          const newAlert = { id: crypto.randomUUID(), createdAt: new Date(), ...showEvent.payload };
          return { activeAlert: newAlert };
        }),
        target: '.$_SHOWING',
      },
    ],
  },
  states: {
    $_IDLE: {
      always: [
        {
          target: '$_SHOWING',
          guard: ({ context }) => context.alerts.length > 0,
          actions: 'showAlert',
        },
      ],
    },
    $_SHOWING: {
      // [MCV-1800] A persistent alert is shown from $_PERSISTENT instead: leaving here on
      // entry cancels the auto-dismiss timer below before it ever fires.
      always: {
        target: '$_PERSISTENT',
        guard: ({ context }) => context.activeAlert?.persistent === true,
      },
      after: {
        ALERT_TIMEOUT: [
          {
            // [MCV-1565] `reenter` is load-bearing, not decoration. xstate v5
            // defaults it to false (formatTransition: `reenter ?? false`), which
            // makes a self-targeting transition INTERNAL -- the state is neither
            // exited nor entered. But `after` compiles to a `raise(...,{delay})`
            // pushed onto entry and a matching `cancel` on exit, so the timer
            // lives exclusively in entry/exit. Without `reenter`, `showAlert`
            // still ran and the toast visibly changed, so the queue looked like
            // it worked -- while no new timer was ever scheduled. The promoted
            // alert then sat forever with zero pending timers, and because the
            // ON_SHOW_ALERT queue-only branch above guards on `!!activeAlert`,
            // every later alert in the session was silently swallowed behind it.
            // Only a page reload recovered. (v4 made targeted self-transitions
            // external by default, so this arrived with the v5 migration.)
            reenter: true,
            target: '$_SHOWING',
            actions: 'showAlert',
            guard: ({ context }) => context.alerts.length > 0,
          },
          {
            target: '$_IDLE',
            actions: 'hideAlert',
          },
        ],
      },
      on: {
        ON_HIDE_ALERT: {
          target: '$_IDLE',
          actions: 'hideAlert',
        },
        ON_DISMISS_ALERT: [
          {
            // [MCV-1565] Dismissing a QUEUED alert must stay internal: the alert
            // on screen is untouched, so cancelling and rescheduling its timer
            // would silently extend it.
            guard: ({ context, event }) => context.activeAlert?.id !== event.payload.id,
            actions: 'dismissAlert',
          },
          {
            // [MCV-1565] Dismissing the ACTIVE alert has to leave $_SHOWING.
            // This was previously targetless, so clicking the X cleared
            // `activeAlert` but left the machine parked in $_SHOWING with an
            // orphaned queue and no pending timer. The stranded entry then
            // resurfaced minutes later, riding the next alert's expiry -- a
            // stale toast appearing after an unrelated save.
            //
            // Routing through $_IDLE reuses its `always` promotion, so the next
            // queued alert is entered normally and gets its own fresh timer.
            target: '$_IDLE',
            actions: 'dismissAlert',
          },
        ],
      },
    },
    // [MCV-1800] An alert that stays until the user closes it (its X, or ON_HIDE_ALERT). No
    // timer runs, so it must not hold back the app's other alerts: a newcomer is shown at once
    // and this one goes back to the FRONT of the queue, so it returns as soon as the newcomer is
    // done (promotion re-enters $_PERSISTENT through $_SHOWING's `always`). A persistent alert
    // therefore leaves only when the user closes it.
    $_PERSISTENT: {
      on: {
        // This state-level handler takes precedence over the root one, which would queue.
        ON_SHOW_ALERT: {
          target: '$_SHOWING',
          actions: assign(({ context, event }) => {
            const showEvent = event as Extract<AlertViewEvents, { type: 'ON_SHOW_ALERT' }>;
            const newAlert = {
              id: crypto.randomUUID(),
              createdAt: new Date(),
              ...showEvent.payload,
            };
            return {
              alerts: context.activeAlert
                ? [context.activeAlert, ...context.alerts]
                : context.alerts,
              activeAlert: newAlert,
            };
          }),
        },
        ON_HIDE_ALERT: {
          target: '$_IDLE',
          actions: 'hideAlert',
        },
        ON_DISMISS_ALERT: [
          {
            // A queued alert: the persistent one on screen is untouched.
            guard: ({ context, event }) => context.activeAlert?.id !== event.payload.id,
            actions: 'dismissAlert',
          },
          {
            // The one on screen: through $_IDLE, which promotes the next queued alert.
            target: '$_IDLE',
            actions: 'dismissAlert',
          },
        ],
      },
    },
  },
});
